import apiClient from '@/api/client'

export interface PendingParentResponse {
  id: string
  first_name: string
  last_name: string
  email: string
}

export interface ParentResponse {
  id: string
  user_id: string
  first_name?: string
  last_name?: string
  email?: string
  cell_phone?: string
  address?: string
  children_count?: number
  emergency_phone_numbers?: string[]
}

export interface ParentProfileResponse {
  id: string
  user_id: string
  emergency_phone_numbers: string[]
}

export interface ParentProfileUpdate {
  emergency_phone_numbers?: string[]
}

export interface MergeParentRequest {
  primary_parent_id: string
  duplicate_parent_id: string
}

export interface ParentRegistrationPayload {
  email: string
  password: string
  first_name: string
  last_name: string
  cell_phone?: string
  address?: string
  city?: string
  state?: string
  zip_code?: string
  emergency_phone_numbers?: string[]
  is_parishioner: boolean
  parish_sharecode: string
  children: Array<{
    first_name: string
    last_name: string
    date_of_birth: string
    sacraments_received?: string[]
    allergen_alerts?: string[]
    is_parishioner?: boolean
  }>
}

export interface ParentRegistrationResponse {
  success?: boolean
  message?: string
  potential_duplicate?: boolean
  existing_email?: string
}

export interface ParishInfo {
  id: string
  name: string
  sharecode: string
}

export const parentsApi = {
  async listPending(): Promise<PendingParentResponse[]> {
    const { data } = await apiClient.get('/api/v1/parents/pending')
    return data
  },
  async approve(id: string): Promise<{ ok: true }> {
    const { data } = await apiClient.post(`/api/v1/parents/${id}/approve`)
    return data
  },
  async reject(id: string): Promise<{ ok: true }> {
    const { data } = await apiClient.post(`/api/v1/parents/${id}/reject`)
    return data
  },
  async list(): Promise<ParentResponse[]> {
    const { data } = await apiClient.get('/api/v1/parents')
    return Array.isArray(data) ? data : []
  },
  async merge(primaryId: string, duplicateId: string): Promise<{ message: string; children_moved: number }> {
    const { data } = await apiClient.post('/api/v1/parents/merge', {
      primary_parent_id: primaryId,
      duplicate_parent_id: duplicateId,
    })
    return data
  },
  async getProfile(parentId: string): Promise<ParentProfileResponse> {
    const { data } = await apiClient.get<ParentProfileResponse>(`/api/v1/parents/${parentId}`)
    return data
  },
  async getMyProfile(): Promise<ParentProfileResponse> {
    try {
      const { data } = await apiClient.get<ParentProfileResponse>('/api/v1/parents/me')
      return data
    } catch {
      throw new Error('Parent profile endpoint not available')
    }
  },
  async updateProfile(parentId: string, update: ParentProfileUpdate): Promise<ParentProfileResponse> {
    const { data } = await apiClient.put<ParentProfileResponse>(`/api/v1/parents/${parentId}`, update)
    return data
  },
  async updateMyProfile(update: ParentProfileUpdate): Promise<ParentProfileResponse> {
    try {
      const { data } = await apiClient.put<ParentProfileResponse>('/api/v1/parents/me', update)
      return data
    } catch {
      throw new Error('Parent profile update endpoint not available')
    }
  },
  async register(payload: ParentRegistrationPayload): Promise<ParentRegistrationResponse> {
    const { data } = await apiClient.post<ParentRegistrationResponse>('/api/v1/parents/register', payload)
    return data
  },
  async validateSharecode(sharecode: string): Promise<ParishInfo | null> {
    try {
      // Public endpoint: returns only the parish name for a valid sharecode
      const { data } = await apiClient.get(`/api/v1/parishes/lookup/${encodeURIComponent(sharecode)}`)
      return { id: '', name: data.name, sharecode: data.sharecode }
    } catch {
      return null
    }
  },
}
